import { parseISO } from "date-fns";
import { z } from "zod";
import { createBlankDraft, FieldWidgetSchema, getBuilderWarnings, serializeFormDefinition } from "@froppio/form-core";
import { createField } from "@/features/form-builder/utils/builderOperations";

export const FormNameSchema = z.string().trim().min(1, "Enter a form name.")
  .refine(name => Array.from(name).length <= 100, "Use 100 characters or fewer.");

const FormSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  versionId: z.string(),
  version: z.number().int().positive(),
  name: z.string(),
  definition: z.string(),
  createdAt: z.date(),
  modifiedAt: z.date(),
  createdBy: z.string(),
  modifiedBy: z.string(),
  versionCreatedAt: z.date(),
  versionCreatedBy: z.string(),
});

export const CreateFormRequestSchema = z.object({
  name: FormNameSchema,
  definition: z.string(),
});
export const UpdateFormRequestSchema = CreateFormRequestSchema.extend({
  expectedVersion: z.number().int().positive().max(2147483647),
});

const FormDtoSchema = z.object({
  id: z.string(),
  business_id: z.string(),
  version_id: z.string(),
  version: z.number().int().positive(),
  name: z.string(),
  definition: z.string(),
  created_at: z.string(),
  modified_at: z.string(),
  created_by: z.string(),
  modified_by: z.string(),
  version_created_at: z.string(),
  version_created_by: z.string(),
});
export const CreateFormDtoSchema = z.object({ name: FormNameSchema, definition: z.string() });
export const UpdateFormDtoSchema = CreateFormDtoSchema.extend({
  expected_version: z.number().int().positive().max(2147483647),
});

export type FormDto = z.infer<typeof FormDtoSchema>;
export type CreateFormDto = z.infer<typeof CreateFormDtoSchema>;
export type UpdateFormDto = z.infer<typeof UpdateFormDtoSchema>;
export type Form = z.infer<typeof FormSchema>;
export type CreateFormRequest = z.infer<typeof CreateFormRequestSchema>;
export type UpdateFormRequest = z.infer<typeof UpdateFormRequestSchema>;

export const FormCapabilitiesDtoSchema = z.object({ ai_generation: z.boolean() });
export type FormCapabilitiesDto = z.infer<typeof FormCapabilitiesDtoSchema>;

const GeneratedFormDtoSchema = z.object({
  name: FormNameSchema,
  questions: z.array(z.object({
    widget: FieldWidgetSchema,
    label: z.string().trim().min(1).max(200),
    help_text: z.string().max(500),
    required: z.boolean(),
    choices: z.array(z.string().trim().min(1).max(100)).max(12),
  })).min(1).max(20),
});
export type GeneratedFormDto = z.infer<typeof GeneratedFormDtoSchema>;

export function fromGeneratedFormDtoToDraft(dto: GeneratedFormDto): CreateFormRequest {
  const parsed = GeneratedFormDtoSchema.safeParse(dto);
  if (!parsed.success) throw new Error("AI returned an invalid form. Please try again.");
  const draft = createBlankDraft();
  parsed.data.questions.forEach(question => {
    const field = createField(question.widget);
    draft.fields[field.id] = {
      ...field, label: question.label, helpText: question.help_text,
      required: question.required,
      choices: question.choices.map(label => ({ value: `choice_${crypto.randomUUID()}`, label })),
    };
    draft.elements.push({ type: "field", fieldId: field.id });
  });
  if (getBuilderWarnings(draft).length) throw new Error("AI returned an incomplete form. Please try again.");
  return { name: parsed.data.name, definition: serializeFormDefinition(draft) };
}

export function fromFormDtoToForm(dto: FormDto): Form {
  const formDto = FormDtoSchema.parse(dto);
  return FormSchema.parse({
    id: formDto.id,
    businessId: formDto.business_id,
    versionId: formDto.version_id,
    version: formDto.version,
    name: formDto.name,
    definition: formDto.definition,
    createdAt: parseISO(formDto.created_at),
    modifiedAt: parseISO(formDto.modified_at),
    createdBy: formDto.created_by,
    modifiedBy: formDto.modified_by,
    versionCreatedAt: parseISO(formDto.version_created_at),
    versionCreatedBy: formDto.version_created_by,
  });
}

export function fromCreateFormRequestToCreateFormDto(request: CreateFormRequest): CreateFormDto {
  const data = CreateFormRequestSchema.parse(request);
  return { name: data.name, definition: data.definition };
}

export function fromUpdateFormRequestToUpdateFormDto(request: UpdateFormRequest): UpdateFormDto {
  const data = UpdateFormRequestSchema.parse(request);
  return { name: data.name, definition: data.definition, expected_version: data.expectedVersion };
}
